import {
    IrpBarrierAnalysisDTO,
    IrpNodeTabDTO,
    IrpReportLocationDTO,
    IrpReportTabDTO,
    IrpWorkbookOverviewDTO,
} from '../../../../../@types/integrated-risk-picture';
import {RegisterRecommendationDTO} from '../../../../../@types/safety-recommendation';
import irpService from '../../../../../services/irp.service';
import componentWorkbookService, {ComponentWorkbookOverview} from '../../../../../services/component-workbook.service';

/**
 * Backend-agnostic fetch surface for the workbook tabs. The scope (which IRP, or which
 * component/source pair) is captured at construction, so tab components only pass the
 * per-request arguments (filters, report id, node id).
 *
 * The IRP implementation forwards every filter argument. The component implementation serves the
 * same DTO shapes from the component evidence endpoints, which do not support server-side filters
 * yet (v1) -- filter arguments are accepted and ignored so the tab code stays identical.
 */
export interface WorkbookDataSource {
    readonly kind: 'irp' | 'component';
    getWorkbookOverview(
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null,
    ): Promise<IrpWorkbookOverviewDTO>;
    getWorkbookReportLocations(
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null,
    ): Promise<IrpReportLocationDTO[]>;
    getWorkbookReportTab(reportId: number): Promise<IrpReportTabDTO>;
    getWorkbookNodeTab(
        nodeId: number,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null,
    ): Promise<IrpNodeTabDTO>;
    getWorkbookBarrierAnalysis(
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null,
    ): Promise<IrpBarrierAnalysisDTO>;
    getWorkbookNtsbRecommendations(
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null,
    ): Promise<RegisterRecommendationDTO[]>;
}

/** Data source backed by the IRP workbook endpoints. */
export const createIrpWorkbookDataSource = (irpId: number): WorkbookDataSource => ({
    kind: 'irp',
    getWorkbookOverview: (dateFrom, dateTo, descriptorTermIds, sourceNames): Promise<IrpWorkbookOverviewDTO> =>
        irpService.getWorkbookOverview(irpId, dateFrom, dateTo, descriptorTermIds, sourceNames),
    getWorkbookReportLocations: (dateFrom, dateTo, descriptorTermIds, sourceNames): Promise<IrpReportLocationDTO[]> =>
        irpService.getWorkbookReportLocations(irpId, dateFrom, dateTo, descriptorTermIds, sourceNames),
    getWorkbookReportTab: (reportId): Promise<IrpReportTabDTO> =>
        irpService.getWorkbookReportTab(irpId, reportId),
    getWorkbookNodeTab: (nodeId, dateFrom, dateTo, descriptorTermIds, sourceNames): Promise<IrpNodeTabDTO> =>
        irpService.getWorkbookNodeTab(irpId, nodeId, 'FAULT_TREE_NODE', dateFrom, dateTo, descriptorTermIds, sourceNames),
    getWorkbookBarrierAnalysis: (dateFrom, dateTo, descriptorTermIds): Promise<IrpBarrierAnalysisDTO> =>
        irpService.getWorkbookBarrierAnalysis(irpId, dateFrom, dateTo, descriptorTermIds),
    getWorkbookNtsbRecommendations: (dateFrom, dateTo, descriptorTermIds, sourceNames): Promise<RegisterRecommendationDTO[]> =>
        irpService.getWorkbookNtsbRecommendations(irpId, dateFrom, dateTo, descriptorTermIds, sourceNames),
});

/**
 * Data source backed by the component evidence workbook endpoints, scoped to one
 * (component, report source) pair.
 *
 * The component overview endpoint wraps the IRP overview shape with the evidence lifecycle
 * (when the last run finished); {@code onOverviewWrapper} lets the provider
 * surface those wrapper fields (e.g. a certification header) without a second fetch. Report
 * locations have no component endpoint, so the map/timeline simply receive no location data.
 */
export const createComponentWorkbookDataSource = (
    componentId: number,
    sourceName: string,
    onOverviewWrapper?: (wrapper: ComponentWorkbookOverview) => void,
): WorkbookDataSource => ({
    kind: 'component',
    getWorkbookOverview: (): Promise<IrpWorkbookOverviewDTO> =>
        componentWorkbookService.getWorkbookOverview(componentId, sourceName)
            .then((wrapper) => {
                onOverviewWrapper?.(wrapper);
                return wrapper.overview;
            }),
    getWorkbookReportLocations: (): Promise<IrpReportLocationDTO[]> => Promise.resolve([]),
    getWorkbookReportTab: (reportId): Promise<IrpReportTabDTO> =>
        componentWorkbookService.getWorkbookReportTab(componentId, sourceName, reportId),
    getWorkbookNodeTab: (nodeId): Promise<IrpNodeTabDTO> =>
        componentWorkbookService.getWorkbookNodeTab(componentId, sourceName, nodeId),
    getWorkbookBarrierAnalysis: (): Promise<IrpBarrierAnalysisDTO> =>
        componentWorkbookService.getWorkbookBarrierAnalysis(componentId, sourceName),
    getWorkbookNtsbRecommendations: (): Promise<RegisterRecommendationDTO[]> =>
        componentWorkbookService.getWorkbookNtsbRecommendations(componentId, sourceName),
});
